import { Chart, TooltipFormatterContextObject, PointOptionsObject, SeriesOptionsType } from 'highcharts/highstock';
import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    OnDestroy,
    NgZone,
    ViewChild,
    ElementRef,
    AfterViewInit,
    Inject,
} from '@angular/core';

import { Theme } from '../../../ui/styles/theme';
import { WINDOW } from '../../../app/injection-tokens';

/**
 * This function is designed to create an options object for timeseries charts. The options can be customized and
 * passed back through the options input in the component. The most common and important options are provided as
 * parameters.
 */
export function createTimeseriesChartOptions(
    tooltipHtmlBuilder: (context: TooltipFormatterContextObject) => string,
    formatYAxis: (y: number) => string,
    plotLines: YAxisPlotLinesOptions[],
    historicalSeries: SeriesOptionsType,
    projectedSeries: SeriesOptionsType,
): Highcharts.Options {
    return {
        tooltip: {
            enabled: true,
            shared: true,
            // Hide the tooltip immediately when the pointer leaves a point, rather
            // than letting it linger for Highcharts' default 500ms hideDelay.
            hideDelay: 0,
            shadow: false,
            backgroundColor: Theme.tooltip.backgroundColor,
            borderWidth: 1,
            borderColor: Theme.tooltip.borderColor,
            useHTML: true,
            // Render the tooltip inside the chart container rather than appended to <body>.
            // These charts live inside Hive dialogs (CDK overlays); an `outside: true`
            // tooltip is body-appended and its show/positioning breaks inside the overlay
            // (it stays invisible on hover — CLOUD ticket), so it must render in the
            // chart's own coordinate space / stacking context instead.
            outside: false,
            style: {
                border: 'none',
                padding: '0',
                cursor: 'default',
                fontSize: '12px',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
            },
            formatter: function (): string {
                return tooltipHtmlBuilder(this);
            },
        },
        chart: {
            spacing: [0, 0, 0, 0],
        },
        title: {
            text: null,
        },
        xAxis: {
            type: 'datetime',
            labels: {
                enabled: true,
                y: 18,
                style: {
                    color: '#A8A8A8',
                },
            },
            title: {
                text: null,
            },
            tickLength: 0,
            crosshair: null,
        },
        yAxis: {
            labels: {
                enabled: true,
                style: {
                    color: '#A8A8A8',
                },
                formatter: function (): string {
                    return formatYAxis(this.value as number);
                },
                x: -9,
            },
            title: {
                text: null,
            },
            endOnTick: false,
            plotLines: plotLines,
        },
        plotOptions: {
            series: {
                lineWidth: 1,
                states: {
                    hover: {
                        lineWidth: 1,
                    },
                },
            },
            area: {
                fillOpacity: 0.1,
                step: 'center',
            },
        },
        legend: {
            enabled: false,
        },
        series: [historicalSeries, projectedSeries],
    };
}

@Component({
    selector: 'timeseries-chart',
    templateUrl: './timeseries-chart.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeseriesChartComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() readonly data: PointOptionsObject[];
    @Input() readonly asOfTime: number;
    // The options object is supposed to be created by createBaseChart() and customized by the parent component.
    @Input() readonly options: Highcharts.Options;
    @ViewChild('chartContainer', { static: true }) readonly chartContainer: ElementRef<HTMLDivElement>;

    private chart: Chart;

    constructor(
        @Inject(WINDOW) private readonly window: Window,
        private readonly ngZone: NgZone,
    ) {}

    ngAfterViewInit(): void {
        // Make sure the chart is scaled correctly to its parent container
        this.ngZone.runOutsideAngular(() => {
            this.window.setImmediate(() => {
                this.chart.reflow();
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.chart && changes.options) {
            this.chart.update(this.options);
        }

        if (!this.chart) {
            this.ngZone.runOutsideAngular(() => {
                // The options are created before the chartContainer. Set the renderTo target here
                // after they have both been initialized.
                this.options.chart.renderTo = this.chartContainer.nativeElement;
                this.chart = new Chart(this.options);
            });
        }

        if (changes.data) {
            this.ngZone.runOutsideAngular(() => {
                this.updateChartSeries();
            });
        }

        if (changes.asOfTime) {
            const firstProjectionIndex = (this.data || []).findIndex(dataPoint => dataPoint.x > this.asOfTime);
            if (firstProjectionIndex >= 0) {
                const firstProjectionX = this.data[firstProjectionIndex].x;
                this.ngZone.runOutsideAngular(() => {
                    this.updateChartSeries();
                    this.updateCurrentBar(firstProjectionX);
                });
            }
        }
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private updateChartSeries(): void {
        const firstProjectionIndex = (this.data || []).findIndex(dataPoint => dataPoint.x > this.asOfTime);
        const historicalPoints = this.data.filter((_, i) => i < firstProjectionIndex);
        const projectedPoints = this.data.filter((_, i) => i >= firstProjectionIndex);
        historicalPoints.push(projectedPoints[0]);
        this.chart.series[0].setData(historicalPoints);
        this.chart.series[1].setData(projectedPoints);
    }

    private updateCurrentBar(firstProjectionX): void {
        this.chart.update({
            xAxis: {
                plotLines: [
                    {
                        color: '#cccccc',
                        value: firstProjectionX,
                        width: 1,
                        zIndex: 2,
                    },
                ],
            },
        });
    }

    isHistoricalPoint(x: number): boolean {
        return x <= this.asOfTime;
    }
}
